import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/translations';

// SVG Flag components for better cross-browser/system compatibility
const USFlag = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg">
    <g fillRule="evenodd">
      <g strokeWidth="1pt">
        <path fill="#bd3d44" d="M0 0h972.8v39.4H0zm0 78.8h972.8v39.4H0zm0 78.7h972.8V197H0zm0 78.8h972.8v39.4H0zm0 78.8h972.8v39.4H0zm0 78.7h972.8v39.4H0zm0 78.8h972.8V512H0z" transform="scale(.9375 .9375)"/>
        <path fill="#fff" d="M0 39.4h972.8v39.4H0zm0 78.8h972.8v39.3H0zm0 78.7h972.8v39.4H0zm0 78.8h972.8v39.4H0zm0 78.8h972.8v39.4H0zm0 78.7h972.8v39.4H0z" transform="scale(.9375 .9375)"/>
      </g>
      <path fill="#192f5d" d="M0 0h389.1v275.7H0z" transform="scale(.9375 .9375)"/>
    </g>
  </svg>
);

const BrazilFlag = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg">
    <g strokeWidth="1pt">
      <path fill="#229e45" fillRule="evenodd" d="M0 0h640v480H0z"/>
      <path fill="#f8e509" fillRule="evenodd" d="m323.4 42.2 280.7 198-280.7 198.3L39.5 240.2z"/>
      <circle cx="320" cy="240" r="105.5" fill="#2b49a3"/>
      <path fill="#ffffef" fillRule="evenodd" d="M195.8 225.3c-6.6 28.5 1.5 57.6 19.2 80.5 50.3-13.3 104.7-13.6 155.1-.7 17.5-22.6 25.8-52.1 19.1-80.7-50.8-13.1-104.5-13-155.1.9z"/>
    </g>
  </svg>
);

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const t = getTranslation(language);

  const languages = [
    { code: 'en', name: t.languageSelector.en, Flag: USFlag },
    { code: 'pt', name: t.languageSelector.pt, Flag: BrazilFlag },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="language-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-optimus-gold/40 bg-optimus-charcoal/50 backdrop-blur-sm transition-all duration-300"
        data-testid="language-selector-trigger"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <currentLang.Flag className="w-5 h-4 rounded-sm object-cover" />
        <span className="font-sub text-xs uppercase tracking-wider text-optimus-white/80 hidden sm:inline">
          {currentLang.code.toUpperCase()}
        </span>
        <ChevronDown 
          size={14} 
          className={`text-optimus-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 w-44 rounded-lg border border-white/10 bg-optimus-charcoal/95 backdrop-blur-xl shadow-xl shadow-black/40 overflow-hidden z-50"
          role="listbox"
          data-testid="language-dropdown"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${
                language === lang.code 
                  ? 'bg-optimus-gold/10 text-optimus-gold' 
                  : 'text-optimus-white/80 hover:bg-white/5 hover:text-optimus-white'
              }`}
              role="option"
              aria-selected={language === lang.code}
              data-testid={`language-option-${lang.code}`}
            >
              <lang.Flag className="w-6 h-5 rounded-sm object-cover" />
              <span className="font-sub text-sm font-medium">{lang.name}</span>
              {language === lang.code && (
                <span className="ml-auto w-2 h-2 rounded-full bg-optimus-gold" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
